import { useTranslation } from "react-i18next";

import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { cn } from "../../../../lib/utils";
import ClaimDialog from "./claimDialog";

/**
 * Everything the customer told us when the claim was filed.
 *
 * Lifted out of `claimHeader`, which had grown a full modal inside it — two
 * hand-written columns of `["Label", value]` pairs with the labels hardcoded in
 * English, even though `trackClaimFlow.claimDetailCard` already held every one
 * of them in both languages.
 *
 * Now one list rendered twice as wide when there is room. The accident
 * description is prose, so it spans the full width rather than being squeezed
 * into a column and wrapping every four words.
 */
/**
 * A missing value is spelled out rather than shown as an em dash. "—" asks the
 * customer to work out whether the field is empty, still loading, or broken;
 * "Not provided" answers it, and it has an Arabic form, which a dash does not.
 * Styled lighter than real data so the eye still skips it.
 */
const Field = ({ label, value, emptyLabel }) => {
  const hasValue = Boolean(value && String(value).trim());
  return (
    <div className="border-b border-[#EDF1F3] py-2.5 last:border-b-0">
      <dt className="text-xs font-normal leading-4 text-[#6E7C87]">{label}</dt>
      <dd
        className={cn(
          "mt-1 break-words text-sm leading-5",
          hasValue
            ? "font-semibold text-[#1A2024]"
            : "font-normal italic text-[#9AA6AC]"
        )}
      >
        {hasValue ? value : emptyLabel}
      </dd>
    </div>
  );
};

const ClaimInfoDialog = ({ open, onClose, details }) => {
  const { t } = useTranslation();
  const translate = useLanguageTranslate();
  const info = details?.ClaimInformation || {};
  const label = (key) => t(`motorInsurance.trackClaimFlow.claimDetailCard.${key}`);
  const notProvided = translate("Not provided", "غير متوفر");

  const fields = [
    [label("claimType"), translate("Motor Policy", "وثيقة مركبات")],
    [label("policyPlan"), info.PolicyPlan],
    [label("driverCprNumber"), info.DriverCPR],
    [label("vehicleOwnerCprNumber"), info.OwnerCPR],
    [label("plateNumber"), info.PlateNumber],
    [label("emailAddress"), info.Email],
    [label("mobileNumber"), info.MobileNumber],
    [label("plannedGarageWorkshop"), info.PreferredGarage],
    [label("repairSurveyDate"), info.SurveyDate],
    [label("claimResolution"), info.ClaimResolution],
  ];

  return (
    <ClaimDialog
      open={open}
      onClose={onClose}
      title={t("motorInsurance.trackClaimFlow.breadcrumb.claim_details")}
      description={translate(
        "The information submitted when this claim was filed.",
        "المعلومات التي تم تقديمها عند فتح هذه المطالبة."
      )}
      contentClassName="sm:max-w-3xl"
    >
      <dl className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
        {fields.map(([fieldLabel, value]) => (
          <Field
            key={fieldLabel}
            label={fieldLabel}
            value={value}
            emptyLabel={notProvided}
          />
        ))}
      </dl>

      <div className="mt-5 rounded-xl bg-[#F6F8F9] p-4">
        <p className="text-xs font-normal leading-4 text-[#6E7C87]">
          {label("accidentDescription")}
        </p>
        <p
          className={cn(
            "mt-1.5 whitespace-pre-line text-sm leading-6",
            info.Description
              ? "text-[#1A2024]"
              : "italic text-[#9AA6AC]"
          )}
        >
          {info.Description ||
            translate(
              "No description was given when this claim was filed.",
              "لم يتم إدخال وصف عند فتح هذه المطالبة."
            )}
        </p>
      </div>
    </ClaimDialog>
  );
};

export default ClaimInfoDialog;
